import { useCallback, useState } from "react";
import EntitySearch from "../../parts/EntitySearch";
import Api from "../../Api";
import moment from "moment";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import BoolTabs from "../../parts/BoolTabs";
import { getAmounts, intToDec } from "../../utils";
import Section from "../../parts/Section";
import DiscreetMessage from "../../parts/DiscreetMessage";
import { QuotesAdminListResponse } from "../../Api";
import { Quote } from "../../types";
import { useSearchParams } from "react-router-dom";

const QuotesAdminList = () => {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<QuotesAdminListResponse[] | null>(null);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [expanded, setExpanded] = useState<string | null>(null);

  const showApproved = searchParams.get("tab") === "aprovados";

  const searchFunction = useCallback(
    (searchTerm: string, page: string) =>
      showApproved
        ? Api.quotesAdminList("approved", searchTerm, page)
        : Api.quotesAdminList("pending", searchTerm, page),
    [showApproved]
  );

  return (
    <div>
      <div className="border-b">
        <Section className="p-4">
          <EntitySearch
            searchFunction={searchFunction}
            setEntities={setData}
            key={lastRefresh}
          />
        </Section>
      </div>

      <BoolTabs
        defaultTab={{ label: "Pendentes", value: "pendentes" }}
        alternateTab={{ label: "Aprovados", value: "aprovados" }}
        onChange={() => setData(null)}
      />

      <div className="flex-1">
        <Section className="p-4">
          {!data && <DiscreetMessage text="Carregando ..." />}

          {data && data.length === 0 && (
            <DiscreetMessage text="Nenhum resultado" />
          )}

          {data && (
            <div className="flex flex-col gap-4 pb-32">
              {data.map(({ quote, client, seller }) => (
                <QuoteCard
                  key={quote.uuid}
                  quote={quote}
                  clientName={client.name}
                  clientEmail={client.email}
                  sellerName={`${seller.firstName} ${seller.lastName}`.trim()}
                  onAction={() => setLastRefresh(Date.now())}
                  expanded={expanded === quote.uuid}
                  setExpanded={(expanded) =>
                    expanded ? setExpanded(quote.uuid) : setExpanded(null)
                  }
                />
              ))}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
};

export default QuotesAdminList;

const QuoteCard = ({
  quote,
  clientName,
  clientEmail,
  sellerName,
  onAction,
  expanded,
  setExpanded,
}: {
  quote: Quote;
  clientName: string;
  clientEmail: string;
  sellerName: string;
  onAction: () => void;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
}) => {
  const amounts = getAmounts(quote);

  return (
    <div className="border rounded-lg shadow-sm" key={quote.uuid}>
      <div
        className={classNames("p-4 flex flex-col gap-2", {
          "hover:bg-slate-50 cursor-pointer": !expanded,
        })}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex gap-1 overflow-scroll no-scrollbar">
          <Badge className="font-mono cursor-default">{quote.number}</Badge>

          <Badge>
            <FontAwesomeIcon icon="calendar" className="mr-2 text-gray-400" />
            {moment(quote.creationDate).format("DD/MM/YY – HH:mm")}
          </Badge>

          <Badge>
            <FontAwesomeIcon icon="user" className="mr-2 text-gray-400" />
            {sellerName}
          </Badge>
        </div>

        <div className="overflow-scroll whitespace-nowrap no-scrollbar font-semibold">
          {clientName}
        </div>

        <div className="flex gap-1 overflow-scroll no-scrollbar">
          {amounts.finalSale > 0 && (
            <div className="bg-cyan-100 text-cyan-800 rounded text-xs px-1.5 py-0.5 tracking-tighter whitespace-nowrap">
              <span className="font-light">Venda</span>{" "}
              <span className="font-bold">{intToDec(amounts.finalSale)}</span>
            </div>
          )}

          {amounts.finalRent > 0 && (
            <div className="bg-lime-100 text-lime-800 rounded text-xs px-1.5 py-0.5 tracking-tighter whitespace-nowrap">
              <span className="font-light">Mensal</span>{" "}
              <span className="font-bold">{intToDec(amounts.finalRent)}</span>
            </div>
          )}

          {amounts.fee > 0 && (
            <div className="bg-violet-100 text-violet-900 rounded text-xs px-1.5 py-0.5 tracking-tighter whitespace-nowrap">
              <span className="font-light">Taxa</span>{" "}
              <span className="font-bold">{intToDec(amounts.fee)}</span>
            </div>
          )}

          <div className="flex-1"></div>

          {quote.isApproved && (
            <div
              className="flex bg-gray-100 font-mono font-bold text-xs items-center px-1 rounded gap-1 text-gray-500"
              title={quote.sends
                .map(
                  (send) =>
                    `${moment(send.date).format("DD/MM/YY HH:mm")}\n${send.to}`
                )
                .join("\n\n")}
            >
              <FontAwesomeIcon icon="envelope" />
              {quote.imported && <span>?</span>}
              {!quote.imported && <span>{quote.sends.length}</span>}
            </div>
          )}
        </div>
      </div>

      {quote.isAuthorized && (
        <div className="text-gray-400 truncate text-xs font-light border-t px-4 py-1">
          <span className="font-semibold text-gray-500">
            <FontAwesomeIcon
              icon="check-circle"
              className="text-gray-400 mr-3"
            />
            Autorizado
          </span>
          {quote.authorizationDate && (
            <>
              {" "}
              em {moment(quote.authorizationDate).format("DD/MM/YY")} às{" "}
              {moment(quote.authorizationDate).format("HH:mm")}
            </>
          )}
        </div>
      )}

      {expanded && (
        <QuoteActions
          uuid={quote.uuid}
          to={clientEmail}
          isApproved={quote.isApproved}
          isAuthorized={quote.isAuthorized}
          externalHash={quote.externalHash}
          onAction={onAction}
          onClose={() => setExpanded(false)}
        />
      )}
    </div>
  );
};

const QuoteActions = ({
  uuid,
  to,
  isApproved,
  isAuthorized,
  externalHash,
  onAction,
  onClose,
}: {
  uuid: string;
  to: string;
  isApproved: boolean;
  isAuthorized: boolean;
  externalHash: string;
  onAction: () => void;
  onClose: () => void;
}) => {
  const [mode, setMode] = useState("actions");
  const [email, setEmail] = useState(to);
  const [action, setAction] = useState(false);

  const resend = async (event: React.FormEvent) => {
    event.preventDefault();
    const msg = `Confirmar o reenvio do orçamento por email para ${email} ?`;
    if (window.confirm(msg)) {
      setAction(true);
      await Api.quotesAdminEmail(uuid, email);
      onAction();
    }
  };

  const approve = async () => {
    const msg = `Confirmar aprovação e enviar orçamento por email para ${to} ?`;
    if (window.confirm(msg)) {
      setAction(true);
      await Api.quotesAdminApprove(uuid);
      await Api.quotesAdminEmail(uuid, to);
      onAction();
    }
  };

  const authorize = async () => {
    const msg = `Confirmar autorização manual?`;
    if (window.confirm(msg)) {
      setAction(true);
      await Api.quotesAdminAuthorize(uuid);
      onAction();
    }
  };

  const remove = async () => {
    const msg = `Apagar orçamento permanentemente?`;
    if (window.confirm(msg)) {
      setAction(true);
      await Api.quotesAdminRemove(uuid);
      onAction();
    }
  };

  return (
    <>
      {mode === "actions" && (
        <div className="flex border-t text-xs">
          <div className="flex overflow-scroll no-scrollbar">
            {!isApproved && (
              <Link
                className="p-3 border-r font-bold hover:bg-slate-50 rounded-bl-lg"
                to={`/aprovar-orcamentos/visualizar/${uuid}`}
                target="_blank"
              >
                Abrir
              </Link>
            )}

            {isApproved && (
              <Link
                className="p-3 border-r font-bold hover:bg-slate-50 rounded-bl-lg"
                to={`/externo/proposta/${externalHash}`}
                target="_blank"
              >
                Abrir
              </Link>
            )}

            {isApproved && !isAuthorized && (
              <button
                className="p-3 border-r font-bold hover:bg-slate-50"
                onClick={authorize}
                disabled={action}
              >
                Autorizar
              </button>
            )}

            {!isApproved && (
              <button
                className="p-3 border-r font-bold text-red-800 bg-red-50 hover:bg-red-100"
                onClick={remove}
                disabled={action}
              >
                Apagar
              </button>
            )}

            {!isApproved && (
              <button
                className="p-3 border-r font-bold text-sky-800 bg-sky-50 hover:bg-sky-100"
                onClick={approve}
                disabled={action}
              >
                Aprovar
              </button>
            )}

            {!isApproved && (
              <Link
                className="p-3 border-r font-bold hover:bg-slate-50"
                to={`/aprovar-orcamentos/alterar/${uuid}`}
              >
                Alterar
              </Link>
            )}

            {isApproved && (
              <button
                className="p-3 border-r font-bold hover:bg-slate-50"
                onClick={() => setMode("resend")}
                disabled={action}
              >
                Reenviar
              </button>
            )}

            <Link
              className="p-3 font-bold hover:bg-slate-50"
              to={`/meus-orcamentos/duplicar/${uuid}`}
            >
              Duplicar
            </Link>
          </div>

          <button
            className="rounded-br-lg p-3 flex-1 text-right hover:bg-slate-50 border-l"
            onClick={onClose}
          >
            <FontAwesomeIcon icon="chevron-up" />
          </button>
        </div>
      )}

      {mode === "resend" && (
        <form onSubmit={resend} className="flex border-t text-xs">
          <input
            onClick={(event) => event.stopPropagation()}
            type="email"
            className="flex-1 border rounded px-2 m-2"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            required
          />

          <button
            type="button"
            className="border-l p-3 font-bold hover:bg-slate-50"
            onClick={() => setMode("actions")}
            disabled={action}
          >
            Cancelar
          </button>

          <button
            className="p-3 font-extrabold text-white bg-sky-600 hover:bg-sky-500 disabled:bg-sky-300 rounded-br-lg"
            disabled={action}
          >
            Enviar
          </button>
        </form>
      )}
    </>
  );
};

const Badge = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <span
    className={`text-xs font-bold text-gray-500 bg-gray-100 py-0.5 px-2 rounded-full whitespace-nowrap ${className}`}
    onClick={(e) => e.stopPropagation()}
  >
    {children}
  </span>
);
